import React, { useState } from 'react';
import { BookOpen, Save, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { SelectedAlbum, MarkupType, WalkInAlbumQuote } from '../types';
import { PAPER_OPTIONS, COVER_OPTIONS, VENDOR_NAMES } from '../data/albumData';
import { calculateAlbumCost, calculateMarkup, formatCurrency, generateQuoteNumber } from '../utils/helpers';

interface WalkInAlbumProps { onSave: (q: WalkInAlbumQuote) => void; }

const DESIGN_CHARGES = { Standard: 500, Premium: 1000, Elite: 1500 };

export default function WalkInAlbum({ onSave }: WalkInAlbumProps) {
  const [step, setStep] = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [paperCode, setPaperCode] = useState('');
  const [coverCode, setCoverCode] = useState('');
  const [sheets, setSheets] = useState(20);
  const [numAlbums, setNumAlbums] = useState(1);
  const [designTier, setDesignTier] = useState<'Standard' | 'Premium' | 'Elite'>('Standard');
  const [markupType, setMarkupType] = useState<MarkupType>(45);
  const [saved, setSaved] = useState(false);

  // VENDOR_NAMES is {vendorId: vendorName} - get unique vendor names
  const vendorList = Object.values(VENDOR_NAMES).filter((v, i, a) => a.indexOf(v) === i);
  const papers = PAPER_OPTIONS.filter(p => p.vendorName === vendorName);
  const covers = COVER_OPTIONS.filter(c => c.vendorName === vendorName);
  const sizes = papers.map(p => p.size).filter((v, i, a) => a.indexOf(v) === i);

  const selectedPaper = PAPER_OPTIONS.find(p => p.paperCode === paperCode);
  const selectedCover = COVER_OPTIONS.find(c => c.coverCode === coverCode);
  const designCharge = DESIGN_CHARGES[designTier];

  const albumCost = selectedPaper && selectedCover
    ? calculateAlbumCost({ paperRatePerSheet: selectedPaper.ratePerSheet, coverRate: selectedCover.rate, sheets, numAlbums, designChargeAmount: designCharge })
    : 0;
  const { markupAmount, quotedAmount } = calculateMarkup(albumCost, markupType);

  const handleSave = () => {
    if (!selectedPaper || !selectedCover || !customerName) return;
    const album: SelectedAlbum = {
      vendorId: selectedPaper.vendorId, vendorName: selectedPaper.vendorName,
      size: selectedPaper.size, paperType: selectedPaper.paperType,
      paperCode: selectedPaper.paperCode, paperRatePerSheet: selectedPaper.ratePerSheet,
      coverType: selectedCover.coverType, coverCode: selectedCover.coverCode,
      coverRate: selectedCover.rate, sheets, numAlbums,
      designChargeTier: designTier, designChargeAmount: designCharge,
      totalAlbumCost: albumCost,
    };
    const q: WalkInAlbumQuote = {
      id: `wia_${Date.now()}`, quoteNumber: generateQuoteNumber(),
      customerName, customerPhone, createdAt: new Date().toISOString(),
      status: 'draft', album, markupType, totalCost: albumCost, markupAmount, quotedAmount,
    };
    onSave(q);
    setSaved(true);
  };

  if (saved) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Check size={26} color="white" />
        </div>
        <h2>Album Quote Saved!</h2>
        <p style={{ color: 'var(--text-secondary)' }}>{formatCurrency(quotedAmount)} quoted to {customerName}</p>
      </div>
    );
  }

  const STEPS = ['Customer', 'Vendor', 'Paper & Cover', 'Specs', 'Review'];

  return (
    <div style={{ padding: 28, maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <BookOpen size={20} color="var(--gold)" />
        <div>
          <h1>Walk-in Album Quote</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>Customer brings photos — album only quote</p>
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
              <div className={`step-dot ${i + 1 === step ? 'step-dot-active' : i + 1 < step ? 'step-dot-done' : 'step-dot-inactive'}`}>
                {i + 1 < step ? <Check size={12} /> : i + 1}
              </div>
              <span style={{ fontSize: 11, fontWeight: i + 1 === step ? 600 : 400, color: i + 1 === step ? 'var(--gold)' : i + 1 < step ? 'var(--teal)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {s}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: i + 1 < step ? 'var(--teal)' : 'var(--border)', margin: '0 8px', marginBottom: 20, minWidth: 12 }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Customer */}
      {step === 1 && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Customer Details</h3>
          <div className="form-row">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Name *</label>
              <input className="input" placeholder="Customer name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Phone</label>
              <input className="input" placeholder="Phone number" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!customerName.trim()}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Vendor */}
      {step === 2 && (
        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Select Vendor</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>Choose the album vendor.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {vendorList.map(v => {
              const pCount = PAPER_OPTIONS.filter(p => p.vendorName === v).length;
              const cCount = COVER_OPTIONS.filter(c => c.vendorName === v).length;
              return (
                <button key={v} onClick={() => { setVendorName(v); setPaperCode(''); setCoverCode(''); }}
                  className={`option-card ${vendorName === v ? 'selected' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: vendorName === v ? 'var(--gold-bg)' : 'var(--bg-hover)', border: `1px solid ${vendorName === v ? 'var(--gold)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: vendorName === v ? 'var(--gold)' : 'var(--text-muted)' }}>
                    {v[0]}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: vendorName === v ? 'var(--gold)' : 'var(--text-primary)' }}>{v}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{pCount} paper options · {cCount} covers</div>
                  </div>
                  {vendorName === v && <Check size={16} style={{ marginLeft: 'auto', color: 'var(--gold)' }} />}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)} disabled={!vendorName}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3 — Paper & Cover */}
      {step === 3 && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>{vendorName} — Paper & Cover</h3>
          <div className="form-group">
            <label>Paper Type *</label>
            <select className="select" value={paperCode} onChange={e => setPaperCode(e.target.value)}>
              <option value="">Select paper type</option>
              {sizes.map(size => (
                <optgroup key={size} label={`Size ${size}`}>
                  {papers.filter(p => p.size === size).map(p => (
                    <option key={p.paperCode} value={p.paperCode}>
                      {p.paperType} — ₹{p.ratePerSheet}/sheet
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedPaper && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--gold-bg)', borderRadius: 6, fontSize: 12, color: 'var(--gold)' }}>
                ₹{selectedPaper.ratePerSheet}/sheet · Size {selectedPaper.size}
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Cover Type *</label>
            <select className="select" value={coverCode} onChange={e => setCoverCode(e.target.value)}>
              <option value="">Select cover type</option>
              {covers.map(c => (
                <option key={c.coverCode} value={c.coverCode}>
                  {c.coverType} — ₹{c.rate}
                </option>
              ))}
            </select>
            {selectedCover && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--teal-bg)', borderRadius: 6, fontSize: 12, color: 'var(--teal)' }}>
                ₹{selectedCover.rate} total
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setStep(2)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(4)} disabled={!paperCode || !coverCode}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Specs */}
      {step === 4 && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Album Specifications</h3>
          <div className="form-row">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Number of Sheets</label>
              <input type="number" className="input" min={1} value={sheets} onChange={e => setSheets(Number(e.target.value))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Number of Albums</label>
              <input type="number" className="input" min={1} value={numAlbums} onChange={e => setNumAlbums(Number(e.target.value))} />
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Design Charge Tier</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['Standard', 'Premium', 'Elite'] as const).map(tier => (
                <button key={tier} onClick={() => setDesignTier(tier)}
                  style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s', background: designTier === tier ? 'var(--gold-bg)' : '#fff', color: designTier === tier ? 'var(--gold)' : 'var(--text-secondary)', border: `1.5px solid ${designTier === tier ? 'var(--gold)' : 'var(--border)'}` }}>
                  <div>{tier}</div>
                  <div style={{ fontSize: 11, fontWeight: 400, marginTop: 2 }}>₹{DESIGN_CHARGES[tier]}</div>
                </button>
              ))}
            </div>
          </div>
          {selectedPaper && selectedCover && (
            <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--bg-hover)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>ESTIMATED COST</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Paper ({sheets} × ₹{selectedPaper.ratePerSheet} × {numAlbums})</span>
                <span>{formatCurrency(sheets * selectedPaper.ratePerSheet * numAlbums)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>Cover × {numAlbums}</span>
                <span>{formatCurrency(selectedCover.rate * numAlbums)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>Design × {numAlbums}</span>
                <span>{formatCurrency(designCharge * numAlbums)}</span>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>Total</span>
                <span style={{ color: 'var(--gold)' }}>{formatCurrency(albumCost)}</span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setStep(3)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary" onClick={() => setStep(5)} disabled={sheets < 1}>
              Continue <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 5 — Review & Save */}
      {step === 5 && (
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 12 }}>Order Summary</h3>
            {[
              ['Customer', customerName + (customerPhone ? ` · ${customerPhone}` : '')],
              ['Vendor', vendorName],
              ['Paper', `${selectedPaper?.paperType} (${selectedPaper?.size}) · ₹${selectedPaper?.ratePerSheet}/sheet`],
              ['Cover', `${selectedCover?.coverType} · ₹${selectedCover?.rate}`],
              ['Sheets', `${sheets} sheets × ${numAlbums} album(s)`],
              ['Design', `${designTier} · ₹${designCharge} × ${numAlbums}`],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: 80 }}>{k}</span>
                <span style={{ textAlign: 'right', fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 12 }}>Markup</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {([35, 45, 55] as MarkupType[]).map(pct => (
                <button key={pct} onClick={() => setMarkupType(pct)}
                  style={{ flex: 1, padding: '10px 0', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s', background: markupType === pct ? 'var(--gold)' : '#fff', color: markupType === pct ? '#fff' : 'var(--text-secondary)', border: `1.5px solid ${markupType === pct ? 'var(--gold)' : 'var(--border)'}` }}>
                  {pct}%
                </button>
              ))}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Base cost</span>
              <span>{formatCurrency(albumCost)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Markup ({markupType}%)</span>
              <span style={{ color: 'var(--amber)' }}>{formatCurrency(markupAmount)}</span>
            </div>
            <div className="divider" />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Quote</span>
              <span style={{ fontWeight: 700, fontSize: 22, color: 'var(--gold)' }}>{formatCurrency(quotedAmount)}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-secondary" onClick={() => setStep(4)}><ChevronLeft size={14} /> Back</button>
            <button className="btn btn-primary btn-lg" onClick={handleSave}>
              <Save size={15} /> Save Quote
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
